import { useEffect, useState } from 'react';
import useStoreFilter from './useStoreFilter';
import { message, Popover } from 'antd';
import LazadaPerformanceTable from './LazadaPerformanceTable';
import { useRequest } from 'ahooks';
import { getStorePerfList } from './service';
import { useTranslate } from '@tolgee/react';
import { Container } from './ShopeePerformance';
import useModal from 'hooks/useModal';
import LazadaTrendDrawer from './LazadaTrendDrawer';
import dayjs from 'dayjs';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { CloseOutlined } from '@ant-design/icons';
import { What } from 'views/Aide/Scenario/PolicyTable';

function LazadaPerformance({ departments }: { departments: Dashboard.Department[] }) {
  const { t } = useTranslate();

  const { CSAT_DATA_TIPS_TEST } = useTriggerMapValue();

  const createTrigger = useCreateTrigger();

  const [tooltipVisible, setTooltipVisible] = useState(!CSAT_DATA_TIPS_TEST);

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({
    platform: 'LAZADA',
    departments,
  });

  const dateRender = (x: number) => dayjs().subtract(x, 'days').format('YYYY/MM/DD');

  const filter = (
    <header>
      <section>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
      </section>
      <div>
        {t('数据说明')}
        <Popover
          visible={tooltipVisible}
          onVisibleChange={(visible) => {
            setTooltipVisible(visible);
            if (!visible && !CSAT_DATA_TIPS_TEST) {
              createTrigger({ trigger: 'CSAT_DATA_TIPS_TEST' });
            }
          }}
          trigger={[CSAT_DATA_TIPS_TEST ? 'hover' : 'click']}
          placement="topRight"
          content={
            <div className="max-w-[562px] relative">
              {!CSAT_DATA_TIPS_TEST && (
                <CloseOutlined
                  className="absolute top-[-8px] right-[-8px]"
                  onClick={() => {
                    createTrigger({ trigger: 'CSAT_DATA_TIPS_TEST' });
                    setTooltipVisible(false);
                  }}
                />
              )}
              <div>
                ·{' '}
                {t('买家好评率数据统计区间：x-y', {
                  x: dateRender(57),
                  y: dateRender(2),
                })}
              </div>
              <div>
                ·{' '}
                {t('聊天数据统计区间：x-y', {
                  x: dateRender(7),
                  y: dateRender(1),
                })}
              </div>
            </div>
          }
        >
          <What />
        </Popover>
      </div>
    </header>
  );

  const { visible, show, hide, meta } = useModal<Dashboard.PlatformData.StorePerfData>();

  const drawer = meta && (
    <LazadaTrendDrawer
      key={meta.storeId}
      visible={visible}
      onClose={() => hide()}
      storePerfData={meta}
    />
  );

  const [sortKey, setSortKey] = useState<string>();
  const [sortType, setSortType] = useState<1 | 2>();

  const { loading, data, run } = useRequest(
    (
      page: {
        pageNum: number;
        pageSize: number;
      } = {
        pageNum: 1,
        pageSize: 10,
      },
    ) => {
      return getStorePerfList({
        ...page,
        sortKey,
        sortType,
        platform: 'LAZADA',
        departmentIds: selectedDepartments.map(({ id }) => id),
        storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
        storeIds: storeInfo.list?.map(({ id }) => Number(id)),
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    run();
  }, [run, sortKey, sortType, selectedDepartments, storeType, storeInfo]);

  const table = (
    <LazadaPerformanceTable
      loading={loading}
      dataSource={data?.list}
      pagination={{
        current: data?.pageNum || 1,
        pageSize: data?.pageSize || 10,
        total: data?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
      }}
      onChange={(page: any, _, { order, field }: any) => {
        if (page.current === (data?.pageNum || 1) && page.pageSize === (data?.pageSize || 10)) {
          setSortKey(order && field);
          setSortType(order === 'ascend' ? 1 : order === 'descend' ? 2 : undefined);
        } else {
          run({
            pageNum: page.current,
            pageSize: page.pageSize,
          });
        }
      }}
      onCheckTrend={show}
    />
  );

  return (
    <Container>
      {filter}
      <section>{table}</section>
      {drawer}
    </Container>
  );
}

export default LazadaPerformance;
